import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import { Formik, Field, Form, FormikErrors } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationModal from "../shared/ConfirmationModal";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";
import { Container, Row, Col, Card } from "react-bootstrap";

interface FAQ {
  question: string;
  answer: string;
  type: string;
}

const FAQEdit: FC = () => {
  const navigate = useNavigate();
  const { faq_id } = useParams<{ faq_id: string }>();

  const [faq, setFaq] = useState<FAQ | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [redirectAfterModal, setRedirectAfterModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");

  useEffect(() => {
    const fetchFAQ = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}faqs/${faq_id}`);
        if (response.data) {
          setFaq(response.data);
        } else {
          setModalMessage("FAQ not found.");
          setShowModal(true);
        }
      } catch (error) {
        console.log(error);
        setFailureMessage("Error loading data. Please try again later.");
        setShowFailureModal(true);
      }
    };

    fetchFAQ();
  }, [faq_id]);

  const callApiUpdateFAQ = async (values: FAQ) => {
    const url = `${process.env.REACT_APP_API_URL}faqs/update/${faq_id}`;

    try {
      const token = localStorage.getItem("jwttoken");
      if (!token) {
        setModalMessage("Unauthorized: Please login first.");
        setShowModal(true);
        return;
      }

      const response = await axios.put(url, values, {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
      });

      if (response.status === 200) {
        setModalMessage("Are you sure you want to update this FAQ?");
        setRedirectAfterModal(true);
        setShowModal(true);
      }
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to update FAQ. Please try again.");
      setShowFailureModal(true);
    }
  };

  const handleSubmit = async (values: FAQ) => {
    const hasChanges = JSON.stringify(values) !== JSON.stringify({
      question: faq?.question || "",
      answer: faq?.answer || "",
      type: faq?.type || "",
    });

    if (!hasChanges) {
      setModalMessage("No changes were made.");
      setShowModal(true);
      return;
    }

    await callApiUpdateFAQ(values);
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (redirectAfterModal) {
      setSuccessMessage("FAQ updated successfully!");
      setShowSuccessModal(true);

      setTimeout(() => {
        navigate("/FAQPage");
      }, 3000);
    }
  };

  if (!faq && !modalMessage) {
    return <div>Loading...</div>;
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Edit FAQ</h4>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={{
                  question: faq?.question || "",
                  answer: faq?.answer || "",
                  type: faq?.type || "",
                }}
                validate={(values) => {
                  const errors: FormikErrors<FAQ> = {};

                  if (!values.question.trim()) {
                    errors.question = "Question is required";
                  }
                  if (!values.answer.trim()) {
                    errors.answer = "Answer is required";
                  }
                  if (!values.type.trim()) {
                    errors.type = "Type is required";
                  }

                  return errors;
                }}
                onSubmit={async (values, { resetForm }) => {
                  await handleSubmit(values);
                  resetForm();
                }}
                enableReinitialize
              >
                {({ errors, touched }) => (
                  <Form>
                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="question" className="form-label">Question:</label>
                        <Field name="question" type="text" className="form-control" />
                        {errors.question && touched.question && (
                          <div className="invalid-feedback" style={{ display: "block" }}>{String(errors.question)}</div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="answer" className="form-label">Answer:</label>
                        <Field name="answer" as="textarea" className="form-control" />
                        {errors.answer && touched.answer && (
                          <div className="invalid-feedback" style={{ display: "block" }}>{String(errors.answer)}</div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                           <label htmlFor="type" className="form-label">Type:</label>
                              <Field as="select" name="type" className="form-control">
                                       <option value="">Select Type</option>
                                       <option value="Candidate">Candidate</option>
                                      <option value="Employer">Employer</option>
                                     <option value="Admin">Admin</option>
                               </Field>
                           {errors.type && touched.type && (
                      <div className="invalid-feedback" style={{ display: "block" }}>{String(errors.type)}</div>
                          )}
                      </Col>
                    </Row>



                    <Row className="mt-4">
                      <Col className="text-center">
                        <button type="submit" className="btn btn-success me-2">Update</button>
                        <button type="button" className="btn btn-danger ms-2" onClick={() => navigate("/FAQPage")}>
                          Go Back
                        </button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>

              <ConfirmationModal
                show={showModal}
                onHide={handleModalClose}
                modalMessage={modalMessage}
                operationType="Confirm"
              />
              <NewconfirmationModal
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
                modalMessage={successMessage}
                operationType="Ok"
                timeoutDuration={3000}
              />
              <AllFailureModal
                show={showFailureModal}
                onHide={() => setShowFailureModal(false)}
                modalMessage={failureMessage}
                operationType="Ok"
                timeoutDuration={5000}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FAQEdit;
